import type { PtoStatus } from "@/lib/pto/approval-rules";
import { ApiError, getJson } from "@/lib/time/api-client";

export type UncertainAction = "delete" | "remove" | "askCancel" | "keep";

/** A request's state now: its status, or null when it no longer exists for this person. */
export type RequestState = PtoStatus | null;

/**
 * After an uncertain attempt whose retry failed, did the first attempt land? Judged from the
 * request's real state, never from the error text: delete (and the approver's remove) is done
 * when the request is gone,
 * ask-cancel when it now waits on a cancel, keep when it is approved with no cancel ask.
 */
export function decide(state: RequestState, action: UncertainAction): "done" | "refused" {
  if (action === "delete" || action === "remove") return state === null ? "done" : "refused";
  if (action === "askCancel") return state === "requestdelete" ? "done" : "refused";
  return state === "approved" ? "done" : "refused";
}

export const isTimeout = (err: unknown): boolean => err instanceof ApiError && err.status === 504;

/** Requests whose last attempt may have saved, per action. Module-level so a remount keeps it. */
const uncertain = new Set<string>();
const mark = (action: UncertainAction, id: string) => `${action}:${id}`;

export function markUncertain(action: UncertainAction, id: string): void {
  uncertain.add(mark(action, id));
}

export function clearUncertain(action: UncertainAction, id: string): void {
  uncertain.delete(mark(action, id));
}

export function isUncertain(action: UncertainAction, id: string): boolean {
  return uncertain.has(mark(action, id));
}

/** Reads one request's state; a 404 means it is gone. Any other failure throws. */
export async function readRequestState(id: string): Promise<RequestState> {
  try {
    const body = await getJson<{ status: PtoStatus }>(
      `/api/pto/requests/one?id=${encodeURIComponent(id)}`,
    );
    return body.status;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Runs a write that may have saved on a 504. If an earlier attempt for this id was uncertain and
 * this one fails, the request is read: when its state shows the goal reached, the write is done
 * (resolves `true`); otherwise the real error surfaces. The mark clears once the request is
 * settled (success, done, or a definite refusal) and stays while it is still uncertain.
 */
export async function runUncertain<R>(
  action: UncertainAction,
  id: string,
  send: () => Promise<R>,
  readState: (id: string) => Promise<RequestState> = readRequestState,
): Promise<{ settled: false; result: R } | { settled: true }> {
  try {
    const result = await send();
    clearUncertain(action, id);
    return { settled: false, result };
  } catch (err) {
    if (isTimeout(err)) {
      markUncertain(action, id);
      throw err;
    }
    if (!isUncertain(action, id)) throw err;
    let state: RequestState;
    try {
      state = await readState(id);
    } catch {
      throw err;
    }
    clearUncertain(action, id);
    if (decide(state, action) === "done") return { settled: true };
    throw err;
  }
}
